/**
 * TeamInviteDialog (AC-891, updated AC-899)
 *
 * Modal dialog for inviting a user to a team.
 * Uses UserSearchModal for user discovery, then confirms the invitation.
 * Handles error states: user not found (404), already a member (409),
 * already invited (409).
 *
 * Parent: TeamDetailPage
 */

import React, { useState, useEffect } from 'react';
import { useTeamStore } from '../../store/teamStore';
import UserSearchModal from '../social/UserSearchModal';
import { UserPublicSummary } from '../../types';

interface TeamInviteDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: number;
}

const TeamInviteDialog: React.FC<TeamInviteDialogProps> = ({ open, onClose, teamId }) => {
  const { inviteToTeam } = useTeamStore();
  const [selectedUser, setSelectedUser] = useState<UserPublicSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setError(null);
      setSubmitting(false);
      setShowSearch(true);
    } else {
      setShowSearch(false);
    }
  }, [open]);

  const handleUserSelected = (user: UserPublicSummary) => {
    setSelectedUser(user);
    setShowSearch(false);
    setError(null);
  };

  const handleSearchClose = () => {
    if (!selectedUser) {
      // No user selected yet, close the whole dialog
      onClose();
    } else {
      setShowSearch(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await inviteToTeam(teamId, selectedUser.username);
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setError('User not found.');
      } else if (status === 409) {
        setError('User is already a member or has a pending invitation.');
      } else {
        setError('Failed to send invitation. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show the search modal when in search mode
  if (showSearch) {
    return (
      <UserSearchModal
        isOpen={true}
        onClose={handleSearchClose}
        onSelectUser={handleUserSelected}
        title="Find User to Invite"
        placeholder="Search by username..."
      />
    );
  }

  // Show confirmation dialog after user is selected
  if (!open || !selectedUser) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-member-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3
          id="invite-member-title"
          className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
        >
          Invite Member
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Selected User
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    @{selectedUser.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedUser.public_project_count} public project{selectedUser.public_project_count !== 1 ? 's' : ''} &middot;{' '}
                    {selectedUser.follower_count} follower{selectedUser.follower_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSearch(true)}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Change
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamInviteDialog;
